import React, { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from "motion/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useScrollAnimation } from "hooks/useGSAP";
import { PageLayout } from "components/layout/PageLayout";
import ProjectCard from "components/ProjectCard/ProjectCard";
import PageHero from "components/pageHero/PageHero";
import { Button } from "components/ui/Button";
import { projectsHeader, projects } from "portfolio";
import ProjectsImg from "./ProjectsImg";
import type { Project, ThemeWithOptionalSetterProps } from "types";

interface MotionCardProps {
  repo: Project;
  index: number;
  activeCard: number | null;
  reduceMotion: boolean;
  scrollProgress: ReturnType<typeof useScroll>["scrollYProgress"];
  onActivate: (index: number | null) => void;
  onPreview: (index: number) => void;
  isMobile: boolean;
}

const projectInsights = [
  { label: "Focus", value: "AI + Analytics", accent: "var(--accent)" },
  { label: "Delivery", value: "Cloud Systems", accent: "var(--accent-cool)" },
  {
    label: "Pattern",
    value: "Product Engineering",
    accent: "var(--accent-warm)",
  },
];

const MotionProjectCard: React.FC<MotionCardProps> = ({
  repo,
  index,
  activeCard,
  reduceMotion,
  scrollProgress,
  onActivate,
  onPreview,
  isMobile,
}) => {
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);

  const springTiltX = useSpring(tiltX, {
    stiffness: 220,
    damping: 24,
    mass: 0.45,
  });
  const springTiltY = useSpring(tiltY, {
    stiffness: 220,
    damping: 24,
    mass: 0.45,
  });

  const velocityX = useVelocity(tiltX);
  const velocityY = useVelocity(tiltY);

  const inertialLift = useTransform(() => {
    const vx = Math.abs(velocityX.get());
    const vy = Math.abs(velocityY.get());
    return Math.min(10, (vx + vy) / 46);
  });

  const cardShift = useTransform(
    scrollProgress,
    [0, 1],
    index % 2 === 0 ? [22, -14] : [14, -20],
  );

  const cardY = useTransform(() => {
    if (reduceMotion) return 0;
    return cardShift.get() - inertialLift.get();
  });

  const isActive = activeCard === index;
  const featuredClass = index === 0 ? "md:col-span-2" : "";
  let cardClassName = `relative ${featuredClass}`;
  if (!isActive && activeCard !== null) {
    cardClassName = `relative opacity-90 ${featuredClass}`;
  } else if (isActive) {
    cardClassName = `relative z-10 ${featuredClass}`;
  }

  return (
    <motion.div
      className={`project-card ${cardClassName}`}
      variants={{
        hidden: { opacity: 0, y: 24, scale: 0.98 },
        show: { opacity: 1, y: 0, scale: 1 },
      }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      whileHover={
        reduceMotion
          ? undefined
          : { scale: 1.012, transition: { duration: 0.18 } }
      }
      onMouseMove={(event) => {
        if (reduceMotion) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - bounds.left;
        const y = event.clientY - bounds.top;
        const relX = (x / bounds.width - 0.5) * 2;
        const relY = (y / bounds.height - 0.5) * 2;
        tiltY.set(relX * 7);
        tiltX.set(-relY * 7);
      }}
      onMouseLeave={() => {
        tiltX.set(0);
        tiltY.set(0);
        onActivate(activeCard === index ? null : activeCard);
      }}
      onHoverStart={() => onActivate(index)}
      onClick={(event) => {
        if (!isMobile) return;
        const target = event.target as HTMLElement;
        if (target.closest("a,button")) return;
        onPreview(index);
      }}
      style={
        reduceMotion
          ? undefined
          : {
              opacity: 0,
              y: cardY,
              rotateX: springTiltX,
              rotateY: springTiltY,
              transformPerspective: 1100,
              transformStyle: "preserve-3d",
            }
      }
    >
      <ProjectCard repo={repo} featured={index === 0} />
      <button
        type="button"
        onClick={() => onPreview(index)}
        className="mt-3 w-full rounded-lg border border-border bg-bg-elevated/85 px-4 py-2.5 text-xs font-mono uppercase tracking-[0.12em] text-text-muted md:hidden"
      >
        Quick Preview
      </button>
    </motion.div>
  );
};

const Projects: React.FC<ThemeWithOptionalSetterProps> = (props) => {
  const theme = props.theme;
  const reduceMotion = useReducedMotion();
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() =>
    globalThis.window === undefined
      ? false
      : globalThis.window.innerWidth < 768,
  );
  const gridRef = useRef<HTMLDivElement>(null);
  const { animate: animateBatch } = useScrollAnimation<HTMLDivElement>();

  const { scrollYProgress } = useScroll({
    target: gridRef,
    offset: ["start end", "end start"],
  });

  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, { stiffness: 260, damping: 34 });
  const smoothY = useSpring(pointerY, { stiffness: 260, damping: 34 });

  const spotlight = useMotionTemplate`radial-gradient(320px circle at ${smoothX}px ${smoothY}px, color-mix(in srgb, var(--accent-color) 16%, transparent), transparent 68%)`;

  const activeProject = activeCard === null ? null : projects.data[activeCard];
  const previewProject =
    previewIndex === null ? null : (projects.data[previewIndex] ?? null);

  useEffect(() => {
    const onResize = (): void => {
      setIsMobile(globalThis.window.innerWidth < 768);
      if (globalThis.window.innerWidth >= 768) {
        setPreviewIndex(null);
      }
    };

    globalThis.window.addEventListener("resize", onResize);
    onResize();

    return () => {
      globalThis.window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    // ensure plugin is registered in this module's gsap instance
    gsap.registerPlugin(ScrollTrigger);

    animateBatch(() => {
      // batch reveal for project cards
      ScrollTrigger.batch(".project-card", {
        interval: 0.08,
        batchMax: 6,
        start: "top 85%",
        onEnter: (elements: Element[]) => {
          gsap.to(elements, {
            opacity: 1,
            y: 0,
            stagger: 0.06,
            duration: 0.6,
            ease: "power3.out",
          });
        },
        onLeaveBack: (elements: Element[]) => {
          gsap.set(elements, { opacity: 0, y: 24 });
        },
      });
    });
  }, [animateBatch]);

  return (
    <PageLayout theme={theme} setTheme={props.setTheme}>
      <PageHero
        eyebrow="Selected Work"
        title={projectsHeader.title}
        description={projectsHeader.description}
        highlights={[
          "Analytics Platforms",
          "Enterprise Systems",
          "AI Workflows",
        ]}
        media={<ProjectsImg theme={theme} />}
      />

      <motion.div
        className="mt-10 grid gap-3 sm:grid-cols-3"
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        {projectInsights.map((insight) => (
          <div
            key={insight.label}
            className="relative overflow-hidden rounded-lg border border-border bg-bg-elevated/70 px-4 py-4"
          >
            <div
              className="absolute inset-x-0 top-0 h-0.5"
              style={{
                background: `linear-gradient(90deg, ${insight.accent}, transparent)`,
              }}
            />
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-muted">
              {insight.label}
            </p>
            <p className="mt-2 text-sm font-semibold tracking-normal text-text-primary">
              {insight.value}
            </p>
          </div>
        ))}
      </motion.div>

      <motion.div
        ref={gridRef}
        className="relative mt-12"
        onMouseMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();
          pointerX.set(event.clientX - bounds.left);
          pointerY.set(event.clientY - bounds.top);
        }}
      >
        <motion.div
          className="pointer-events-none absolute inset-0 hidden rounded-2xl md:block"
          style={{ background: spotlight }}
          aria-hidden="true"
        />

        <div className="mb-6 flex flex-col gap-3 border-y border-border py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-text-muted">
            Selected build log
          </p>
          <p className="max-w-xl text-sm leading-relaxed text-text-secondary sm:text-right">
            Hover a project to bring it into focus, or open quick preview on
            mobile for stack and link details.
          </p>
        </div>

        <motion.div
          className="relative grid grid-cols-1 gap-6 md:grid-cols-2"
          initial={reduceMotion ? false : "hidden"}
          whileInView={reduceMotion ? undefined : "show"}
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.08,
                delayChildren: 0.05,
              },
            },
          }}
        >
          {projects.data.map((repo: Project, index: number) => {
            return (
              <MotionProjectCard
                key={repo.id ?? repo.name ?? index}
                repo={repo}
                index={index}
                activeCard={activeCard}
                reduceMotion={Boolean(reduceMotion)}
                scrollProgress={scrollYProgress}
                onActivate={setActiveCard}
                onPreview={(next) => setPreviewIndex(next)}
                isMobile={isMobile}
              />
            );
          })}
        </motion.div>
      </motion.div>

      <AnimatePresence initial={false}>
        {activeProject && (
          <motion.aside
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="sticky bottom-6 z-20 mt-8 hidden rounded-lg border border-border bg-bg-primary/95 px-4 py-3 shadow-lg backdrop-blur md:flex md:items-center md:justify-between"
          >
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-text-muted">
              Focused project
            </p>
            <p className="text-sm font-semibold text-text-primary">
              {activeProject.name}
            </p>
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {previewProject && (
          <>
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px] md:hidden"
              onClick={() => setPreviewIndex(null)}
              aria-label="Close project preview"
            />
            <motion.aside
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 340,
                damping: 34,
                mass: 0.9,
              }}
              className="fixed inset-x-0 bottom-0 z-50 max-h-[78svh] overflow-y-auto rounded-t-2xl border-t border-border bg-bg-primary px-5 pb-8 pt-5 shadow-2xl md:hidden"
            >
              <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-border" />
              <div className="mb-3 flex items-start justify-between gap-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-text-muted">
                  Quick Project Preview
                </p>
                <button
                  type="button"
                  onClick={() => setPreviewIndex(null)}
                  className="rounded-md border border-border px-2.5 py-1 text-xs text-text-muted"
                >
                  Close
                </button>
              </div>
              <h3 className="text-xl font-display font-semibold text-text-primary">
                {previewProject.name}
              </h3>
              {previewProject.description && (
                <p className="mt-3 text-sm leading-relaxed text-text-secondary">
                  {previewProject.description}
                </p>
              )}

              {(previewProject.languages?.length ?? 0) > 0 && (
                <div className="mt-5">
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.11em] text-text-muted">
                    Stack
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(previewProject.languages ?? []).map((language, idx) => (
                      <span
                        key={`${language.name ?? "stack"}-${idx}`}
                        className="rounded-full border border-border bg-bg-elevated px-2.5 py-1 text-xs text-text-muted"
                      >
                        {language.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(previewProject.links?.length ?? 0) > 0 && (
                <div className="mt-6 grid grid-cols-1 gap-2.5">
                  {(previewProject.links ?? []).map((link, idx) => (
                    <a
                      key={`${link.name ?? "link"}-${idx}`}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg border border-border bg-bg-elevated/70 px-3 py-2 text-sm text-text-primary"
                    >
                      {link.name}
                    </a>
                  ))}
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="mt-16 text-center">
        <Button
          variant="outline"
          size="lg"
          href="https://github.com/rexfordkode"
          target="_blank"
          rel="noopener noreferrer"
        >
          More Projects on GitHub
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </Button>
      </div>
    </PageLayout>
  );
};

export default Projects;
